'use client';

import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { getPromptTemplates, adaptPromptOnly } from '@/lib/mcp/api/mcpClient';
import { PromptData } from '@/lib/mcp/types';

interface UsePromptManagementProps {
  generationType: 'hero' | 'image' | 'gemini';
  contentToAdapt: string;
  adaptSlightly: boolean;
  doFront: boolean;
  doSome?: boolean;
  onPromptChange?: (promptData: PromptData) => void;
}

export function usePromptManagement({
  generationType,
  contentToAdapt,
  adaptSlightly,
  doFront,
  doSome = false,
  onPromptChange
}: UsePromptManagementProps) {
  const [promptTemplates, setPromptTemplates] = useState<Record<string, string>>({});
  const [loadingTemplates, setLoadingTemplates] = useState<boolean>(true);
  const [selectedPromptKey, setSelectedPromptKey] = useState<string>('');
  const [basePrompt, setBasePrompt] = useState<string>('');
  const [adaptedPrompt, setAdaptedPrompt] = useState<string>('');
  const [finalPrompt, setFinalPrompt] = useState<string>('');
  const [isAdapting, setIsAdapting] = useState<boolean>(false);
  const { toast } = useToast();

  // Load prompt templates on mount
  useEffect(() => {
    let isMounted = true;
    const fetchPromptTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const templates = await getPromptTemplates();
        if (isMounted) {
          setPromptTemplates(templates);
        }
      } catch (error) {
        console.error('Error fetching prompt templates:', error);
        if (isMounted) {
          setPromptTemplates({});
          toast({
            title: 'Error',
            description: 'Failed to load prompt templates from API',
            variant: 'destructive',
          });
        }
      } finally {
        if (isMounted) {
          setLoadingTemplates(false);
        }
      }
    };

    fetchPromptTemplates();
    return () => { isMounted = false; };
  }, [toast]);

  // Helper to notify parent component of prompt changes
  const notifyPromptChange = useCallback((base: string, adapted: string, final: string) => {
    if (onPromptChange) {
      onPromptChange({
        base_prompt: base,
        adapted_prompt: adapted,
        final_prompt: final
      });
    }
  }, [onPromptChange]);

  // Determine and set the selected prompt key
  useEffect(() => {
    let key = '';
    if (generationType === 'hero') {
      key = doFront ? 'hero-front' : 'hero';
    } else if (generationType === 'image') {
      key = doSome 
        ? (doFront ? 'some-elements-front' : 'some-elements') 
        : (doFront ? 'one-element-front' : 'one-element');
    } else { // gemini or default
      key = 'hero'; // Default to hero prompt 
    }
    console.log(`[usePromptManagement] Setting selectedPromptKey based on props: ${key}`);
    setSelectedPromptKey(key);
  }, [generationType, doFront, doSome]);

  // Update base prompt *only* when selected key changes AND templates are available
  useEffect(() => {
    if (selectedPromptKey && !loadingTemplates && promptTemplates[selectedPromptKey]) {
      console.log(`[usePromptManagement] Setting basePrompt for key: ${selectedPromptKey}`);
      const newBasePrompt = promptTemplates[selectedPromptKey];
      setBasePrompt(newBasePrompt);
      // Reset adaptation when base prompt changes
      setAdaptedPrompt(''); 
      setFinalPrompt(newBasePrompt); // Initially, final is the same as base
      notifyPromptChange(newBasePrompt, '', newBasePrompt);
    } else if (!loadingTemplates) {
       // Handle case where key is invalid or templates haven't loaded the key
       console.log(`[usePromptManagement] No template found for key: ${selectedPromptKey}. Resetting prompts.`);
       setBasePrompt('');
       setAdaptedPrompt('');
       setFinalPrompt('');
       notifyPromptChange('', '', '');
    }
  }, [selectedPromptKey, promptTemplates, loadingTemplates, notifyPromptChange]); 

  // Adapt prompt automatically when relevant inputs change
  useEffect(() => {
    // Only adapt if we have content and a base prompt
    if (!basePrompt || !contentToAdapt) {
      console.log('[usePromptManagement] Skipping adaptation: No base prompt or content.');
      // Ensure final prompt reflects the current base if adaptation isn't happening
      if (finalPrompt !== basePrompt && adaptedPrompt === '') {
         setFinalPrompt(basePrompt);
         notifyPromptChange(basePrompt, '', basePrompt);
      }
      return; 
    }

    let isMounted = true;
    const adapt = async () => {
      console.log(`[usePromptManagement] Adapting prompt for key: ${selectedPromptKey}`);
      setIsAdapting(true);
      try {
        const adapted = await adaptPromptOnly(
          contentToAdapt,
          basePrompt,
          adaptSlightly,
          false 
        );
        if (isMounted) {
          setAdaptedPrompt(adapted);
          setFinalPrompt(adapted); // Set final prompt to the newly adapted one
          notifyPromptChange(basePrompt, adapted, adapted);
        }
      } catch (error) {
        console.error('Error adapting prompt:', error);
        if (isMounted) {
          toast({ title: 'Error', description: 'Failed to adapt prompt', variant: 'destructive' });
          // Fallback: final prompt remains the base prompt
          setAdaptedPrompt('');
          setFinalPrompt(basePrompt);
          notifyPromptChange(basePrompt, '', basePrompt);
        }
      } finally {
        if (isMounted) {
          setIsAdapting(false);
        }
      }
    };

    adapt();
    return () => { isMounted = false; }; // Cleanup

  }, [basePrompt, contentToAdapt, adaptSlightly, selectedPromptKey, notifyPromptChange, toast, adaptedPrompt, finalPrompt]); // Rerun adaptation if base prompt changes

  // Function to manually trigger prompt refresh (adaptation)
  const refreshPrompt = useCallback(async () => {
    if (!basePrompt || !contentToAdapt) {
      toast({ title: 'Error', description: 'Cannot refresh: Base prompt or content is missing.', variant: 'destructive' });
      return;
    }
    console.log(`[usePromptManagement] Manually refreshing prompt adaptation for key: ${selectedPromptKey}`);
    setIsAdapting(true);
    try {
      const adapted = await adaptPromptOnly(contentToAdapt, basePrompt, adaptSlightly, false);
      setAdaptedPrompt(adapted);
      setFinalPrompt(adapted);
      notifyPromptChange(basePrompt, adapted, adapted);
      toast({ title: 'Prompt Refreshed', description: 'Adaptation based on content updated.' });
    } catch (error) {
      console.error('Error refreshing prompt:', error);
      toast({ title: 'Error', description: 'Failed to refresh prompt adaptation.', variant: 'destructive' });
    } finally {
      setIsAdapting(false);
    }
  }, [basePrompt, contentToAdapt, adaptSlightly, selectedPromptKey, notifyPromptChange, toast]);

  // Allow manual update of the final prompt (e.g., user edits textarea)
  const updateFinalPrompt = useCallback((newFinalPrompt: string) => {
    setFinalPrompt(newFinalPrompt);
    // Notify parent about the change, keeping base/adapted as they were
    notifyPromptChange(basePrompt, adaptedPrompt, newFinalPrompt);
  }, [basePrompt, adaptedPrompt, notifyPromptChange]);

  return {
    promptTemplates,
    selectedPromptKey,
    basePrompt,
    adaptedPrompt,
    finalPrompt,
    isAdapting,
    loadingTemplates,
    refreshPrompt,
    updateFinalPrompt,
    setSelectedPromptKey
  };
}